import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';

/** Derives a URL-safe address from what someone types as a name, so they rarely have to think
 * about the second field at all. */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function CreateWorkspaceCard(): ReactNode {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);

  const effectiveSlug = slugEdited ? slug : toSlug(name);

  const create = useMutation({
    mutationFn: () => api.post('/api/v1/tenants', { name: name.trim(), slug: effectiveSlug }),
    onSuccess: async () => {
      await refresh();
    },
  });

  const error = create.error;
  // When the server pointed at a field, the message belongs on that field, not in a banner
  // repeating something less specific above it.
  const fieldError = error instanceof ApiError ? error.fieldError('/slug') : undefined;

  return (
    <section className="card" aria-labelledby="create-workspace-heading">
      <h2 className="card__title" id="create-workspace-heading">
        {t('workspace.create.title')}
      </h2>
      <p className="empty__body">{t('workspace.create.body')}</p>

      <form
        className="form"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          create.mutate();
        }}
        noValidate
      >
        {error instanceof ApiError && fieldError === undefined ? (
          <Alert tone="critical">{t(`error.${error.code}`)}</Alert>
        ) : null}

        <TextField
          label={t('workspace.create.name')}
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <TextField
          label={t('workspace.create.slug')}
          hint={t('workspace.create.slugHint')}
          required
          value={effectiveSlug}
          onChange={(event) => {
            setSlugEdited(true);
            setSlug(toSlug(event.target.value));
          }}
          {...(fieldError === undefined ? {} : { error: fieldError })}
        />

        <Button type="submit" variant="primary" loading={create.isPending}>
          {t('workspace.create.submit')}
        </Button>
      </form>
    </section>
  );
}
